// MaturaTriangleAreaClosedTask.js
// Zadanie maturalne – zamknięte
// Pole trójkąta z dwóch boków i kąta między nimi
// Wzór: P = 1/2 * a * b * sin(α)

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";
import TriangleTwoSidesAngleDiagram from "../../../../components/diagrams/TriangleTwoSidesAngleDiagram";

// === utils ===

// losowa liczba całkowita z [a,b]
const rnd = (a, b) => Math.floor(Math.random() * (b - a + 1)) + a;

// sin ładnych kątów (symbolicznie)
const SIN_INFO = {
  30: { num: 1, den: 2, root: null, latex: "\\tfrac{1}{2}" },
  45: { num: 1, den: 2, root: 2, latex: "\\tfrac{\\sqrt{2}}{2}" },
  60: { num: 1, den: 2, root: 3, latex: "\\tfrac{\\sqrt{3}}{2}" },
  120: { num: 1, den: 2, root: 3, latex: "\\tfrac{\\sqrt{3}}{2}" }, // = sin 60
  135: { num: 1, den: 2, root: 2, latex: "\\tfrac{\\sqrt{2}}{2}" }, // = sin 45
  150: { num: 1, den: 2, root: null, latex: "\\tfrac{1}{2}" }, // = sin 30
};

function gcd(a, b) {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a || 1;
}

// format (k/den)*sqrt(root?) -> LaTeX (ładne całkowite / ułamki / mieszane)
function formatCoeffWithRoot(k, den, root) {
  const d = gcd(Math.abs(k), Math.abs(den));
  let num = k / d;
  let denS = den / d;

  if (!root) {
    const sign = num < 0 ? "-" : "";
    const absNum = Math.abs(num);
    if (denS === 1) return `${num}`;
    if (absNum < denS) {
      return `${sign}\\dfrac{${absNum}}{${denS}}`;
    }
    const whole = Math.floor(absNum / denS);
    const rem = absNum % denS;
    if (rem === 0) return `${sign}${whole}`;
    return `${sign}${whole}\\dfrac{${rem}}{${denS}}`;
  }

  const sign = num < 0 ? "-" : "";
  const nabs = Math.abs(num);
  if (nabs === 1 && denS === 1) return `${sign}\\sqrt{${root}}`;
  if (denS === 1) return `${sign}${nabs}\\sqrt{${root}}`;
  if (nabs === 1) return `${sign}\\dfrac{\\sqrt{${root}}}{${denS}}`;
  return `${sign}\\dfrac{${nabs}\\sqrt{${root}}}{${denS}}`;
}

// === generator ===

function createOriginalTask() {
  const optionLatex = ["6", "6\\sqrt{3}", "12", "12\\sqrt{3}"];
  const options = optionLatex.map((latex, index) => ({
    latex,
    label: ["A", "B", "C", "D"][index],
    isCorrect: index === 1,
  }));

  return {
    type: "MaturaTriangleAreaClosedTask",
    data: {
      AB: 6,
      AC: 4,
      angleA: 60,
      sinInfo: SIN_INFO[60],
      k: 24,
      den: 4,
      root: 3,
      correctNumeric: 6 * Math.sqrt(3),
      options,
      correctIndex: 1,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_SIE",
      taskNumber: "22.1",
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") {
    return createOriginalTask();
  }

  // znane: |AB|, |AC|, kąt przy A = ∠CAB
  const angleOptions = [30, 45, 60, 120, 135, 150];
  const angleA =
    angleOptions[rnd(0, angleOptions.length - 1)]; // kąt przy A
  const sinInfo = SIN_INFO[angleA];

  // ładne długości boków
  const AB = rnd(5, 10);
  const AC = rnd(4, 9);

  // współczynnik dla P = (AB*AC*num)/(2*den)*sqrt(root?)
  const k = AB * AC * (sinInfo.num ?? 1);
  const den = 2 * (sinInfo.den ?? 1);
  const root = sinInfo.root;

  const correctLatex = formatCoeffWithRoot(k, den, root);

  // numeric (tylko na wszelki wypadek)
  const sinNumeric = root
    ? Math.sqrt(root) / sinInfo.den
    : sinInfo.num / sinInfo.den;
  const correctNumeric = 0.5 * AB * AC * sinNumeric;

  // błędne odpowiedzi – wielokrotności
  const variantsKD = [
    { mulK: 1, mulD: 2 }, // P/2
    { mulK: 2, mulD: 1 }, // 2P
    { mulK: 3, mulD: 2 }, // 3/2 P
  ];

  const wrongOptions = variantsKD.map(({ mulK, mulD }) => {
    const kk = k * mulK;
    const dd = den * mulD;
    return {
      latex: formatCoeffWithRoot(kk, dd, root),
    };
  });

  const allLatex = [correctLatex, ...wrongOptions.map((w) => w.latex)];
  const uniqLatex = Array.from(new Set(allLatex)).slice(0, 4);

  while (uniqLatex.length < 4) {
    const extraMul = rnd(2, 5);
    const extraLatex = formatCoeffWithRoot(k * extraMul, den, root);
    if (!uniqLatex.includes(extraLatex)) uniqLatex.push(extraLatex);
  }

  const letters = ["A", "B", "C", "D"];
  const rawOptions = uniqLatex.map((latex) => ({
    latex,
    isCorrect: latex === correctLatex,
  }));

  const shuffled = [...rawOptions];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  const options = shuffled.map((opt, idx) => ({
    ...opt,
    label: letters[idx],
  }));

  const correctIndex = options.findIndex((o) => o.isCorrect);

  return {
    type: "MaturaTriangleAreaClosedTask",
    data: {
      AB,
      AC,
      angleA,
      sinInfo,
      k,
      den,
      root,
      correctNumeric,
      options,
      correctIndex,
    },
  };
}

// === render task ===

export function renderTask({ data }) {
  const { AB, AC, angleA, options } = data;
  const diagramSize = angleA > 90 ? 420 : 300;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne – zamknięte.</strong>
      </p>

      <p>
        Dany jest trójkąt <InlineMath math={'ABC'} />, w którym{" "}
        <InlineMath math={`|AB| = ${AB}`} />,{" "}
        <InlineMath math={`|AC| = ${AC}`} /> oraz{" "}
        <InlineMath
          math={`\\left|\\angle CAB\\right| = ${angleA}^{\\circ}`}
        />
        . (Zobacz rysunek).
      </p>

      {/* pełna szerokość na rysunek, centrowany, większy SVG */}
      <div
        style={{
          marginTop: 4,
          marginBottom: 10,
          width: "100%",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <TriangleTwoSidesAngleDiagram
          size={diagramSize}
          angleDeg={angleA}
          sideAB={AB}
          sideAC={AC}
        />
      </div>

      <p>
        Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.
      </p>
      <p>
        Pole trójkąta <InlineMath math={'ABC'} /> jest równe:
      </p>

      <div style={{ marginTop: 8 }}>
        {options.map((opt) => (
          <div key={opt.label} style={{ marginBottom: 4 }}>
            <InlineMath math={`${opt.label}.\\; ${opt.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

// === input (ABCD) ===

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options } = data;
  const value =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const letterOptions = options.map((opt, idx) => ({
    value: idx,
    label: opt.label,
    textNode: opt.label,
  }));

  const handleChange = (idx) => {
    const opt = options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      latex: opt.latex,
    });
  };

  return (
    <ABCDInput
      options={letterOptions}
      value={value}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

// === check ===

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number") {
    return false;
  }
  const { correctIndex } = data;
  return userAnswer.choiceIndex === correctIndex;
}

// === answer (tylko literka + treść wariantu) ===

export function renderAnswer({ data }) {
  const { options, correctIndex } = data;
  const correct = options?.[correctIndex];
  if (!correct) return null;

  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />
    </div>
  );
}

// === user answer ===

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label ||
    !userAnswer.latex
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const { label, latex } = userAnswer;

  return (
    <div style={{ marginTop: "0.5rem" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${label}.\\; ${latex}`} />
    </div>
  );
}

// === explanation ===

export function renderExplanation({ data }) {
  const { AB, AC, angleA, sinInfo, k, den, root } = data;

  const sinLatex = sinInfo.root
    ? `\\dfrac{\\sqrt{${sinInfo.root}}}{${sinInfo.den}}`
    : `\\dfrac{${sinInfo.num}}{${sinInfo.den}}`;

  const unsimplified = root
    ? `\\dfrac{${AB}\\cdot ${AC}\\cdot \\sqrt{${root}}}{${2 * sinInfo.den}}`
    : `\\dfrac{${AB}\\cdot ${AC}}{${2 * sinInfo.den}}`;

  const simplified = formatCoeffWithRoot(k, den, root);

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        <strong>Krok 1.</strong> Korzystamy ze wzoru na pole trójkąta, gdy znamy
        dwa boki i kąt między nimi:
      </p>

      <BlockMath math={'P = \\tfrac{1}{2}\\,a\\,b\\,\\sin \\gamma'} />

<p>
  <strong>Krok 2.</strong> W tym zadaniu bokami są odcinki <InlineMath math={'AB'} /> i{" "}
  <InlineMath math={'AC'} />, a kątem między nimi jest{" "}
  <span style={{ whiteSpace: "nowrap" }}>
    kąt <InlineMath math={'\\angle CAB'} />:
  </span>
</p>


      <BlockMath
        math={`P = \\tfrac{1}{2}\\cdot ${AB}\\cdot ${AC}\\cdot \\sin ${angleA}^{\\circ}`}
      />

      <p>
        <strong>Krok 3.</strong> Obliczamy wartość sinusa:
      </p>

      <BlockMath math={`\\sin ${angleA}^{\\circ} = ${sinLatex}`} />

      <p>
        <strong>Krok 4.</strong> Podstawiamy i upraszczamy wyrażenie na pole:
      </p>

      <BlockMath
        math={`P = \\tfrac{1}{2}\\cdot ${AB}\\cdot ${AC}\\cdot ${sinLatex}`}
      />

      {unsimplified !== simplified ? (
        <>
          <BlockMath math={`P = ${unsimplified}`} />
          <BlockMath math={`P = ${simplified}`} />
        </>
      ) : (
        <BlockMath math={`P = ${simplified}`} />
      )}
    </div>
  );
}

